import React, { useState } from 'react';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Send, Loader2, CheckCircle2 } from 'lucide-react';
import { newsletterAPI } from '../services/api';

/**
 * Newsletter form
 * variant: 'light' (default) for white/bg-slate backgrounds
 *          'dark' for footer (#0F172A background)
 */
const NewsletterSignup = ({ variant = 'light' }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await newsletterAPI.subscribe({
        email: email.trim(),
        first_name: firstName.trim() || null,
        language: (i18n.language || 'fr').split('-')[0],
      });
      setSubmitted(true);
      toast.success(t('newsletter.success', 'Inscription confirmée. Merci !'));
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          t('newsletter.error', 'Inscription impossible. Vérifiez votre e-mail.'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const isDark = variant === 'dark';
  const inputCls = isDark
    ? 'w-full bg-white/5 border border-white/10 text-white placeholder:text-slate-500 text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-emerald-400 focus:bg-white/10 transition-colors'
    : 'w-full bg-slate-50 border border-slate-200 text-slate-900 placeholder:text-slate-400 text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-blue-900 focus:bg-white transition-colors';
  const disclaimerCls = isDark ? 'text-slate-500' : 'text-slate-400';

  if (submitted) {
    return (
      <div
        className={`flex items-start gap-3 p-4 rounded-xl ${
          isDark ? 'border border-emerald-400/30 bg-emerald-400/5' : 'border border-emerald-200 bg-emerald-50'
        }`}
        data-testid="newsletter-confirmed"
      >
        <CheckCircle2 size={18} className="text-emerald-500 flex-shrink-0 mt-0.5" />
        <p className={`text-sm leading-relaxed ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
          {t('newsletter.confirmed', 'Merci ! Vous recevrez nos prochaines actualités.')}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3" data-testid="newsletter-form">
      <input
        type="text"
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        placeholder={t('newsletter.firstName', 'Prénom')}
        className={inputCls}
        data-testid="newsletter-firstname"
      />
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t('newsletter.email', 'Votre e-mail')}
        className={inputCls}
        data-testid="newsletter-email"
      />
      <button
        type="submit"
        disabled={submitting}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded-full"
        data-testid="newsletter-submit"
      >
        {submitting ? (
          <>
            <Loader2 size={14} className="animate-spin" />
            {t('newsletter.sending', 'Inscription…')}
          </>
        ) : (
          <>
            {t('newsletter.cta', "S'inscrire")}
            <Send size={14} />
          </>
        )}
      </button>
      <p className={`text-[11px] leading-relaxed ${disclaimerCls}`}>
        {t(
          'newsletter.disclaimer',
          'En vous inscrivant, vous acceptez de recevoir nos communications. Désinscription possible à tout moment.',
        )}
      </p>
    </form>
  );
};

export default NewsletterSignup;
